class ContactSmartForm extends HTMLElement {
  connectedCallback() {
    this._step = 1;
    this._totalSteps = 3;
    this._stepLabels = ['CORPORATE IDENTITY & CONTACT', 'INGREDIENT & VOLUME PARAMETERS', 'PARAMETERS, LOGISTICS & COMPLIANCE'];

    this._panels     = this.querySelectorAll('.csf__step-panel');
    this._stepNums   = this.querySelectorAll('.csf__step-number');
    this._trackLabels= this.querySelectorAll('.csf__step-label');
    this._pill       = this.querySelector('.csf__step-pill');
    this._nextBtn    = this.querySelector('[data-csf-next]');
    this._backBtn    = this.querySelector('[data-csf-back]');
    this._form       = this.querySelector('form');

    if (this._nextBtn) this._nextBtn.addEventListener('click', this._onNext.bind(this));
    if (this._backBtn) this._backBtn.addEventListener('click', this._onBack.bind(this));

    // Pressing Enter in any text field submits its form natively — that's a
    // browser default independent of the Next/Submit button's `type`, so it
    // was posting the form (with steps 2-3 still unfilled) whenever someone
    // hit Enter while on step 1 or 2. Treat that the same as clicking Next.
    if (this._form) {
      this._form.addEventListener('submit', (evt) => {
        if (this._step < this._totalSteps) {
          evt.preventDefault();
          this._onNext();
        }
      });
    }

    // Clear error state on input.
    // NOTE: the class here must stay in sync with contact-smart-form.css, which
    // styles .csf__field--error. This previously toggled .csf__input--error — a
    // name that appears nowhere in the CSS — so the red highlight never showed
    // and an invalid field was only signalled by focus moving to it.
    this.querySelectorAll('.csf__field').forEach(field => {
      field.addEventListener('input', () => this._clearFieldError(field));
    });

    // Phone sanitising now lives in phone-input.js, which covers every
    // input[type="tel"] in the theme rather than just this one field, caps the
    // digit count, and preserves the caret when it removes a character. The
    // local handler that used to be here did none of those and would have fought
    // it for the same input event.

    this._render();

    // The success banner no longer replaces the form, so on the post-submit page
    // load it can sit above a full form and go unnoticed — especially if the
    // browser restores the previous scroll position. Bring it into view and move
    // focus to it so it is announced. Guarded so it only runs when the banner is
    // actually rendered (i.e. form.posted_successfully?).
    // Draft persistence. Shopify's contact form object only returns `email` and
    // `body` to the template, so a server-side rejection cannot repopulate the
    // custom fields (Company Name, Job Title, Phone, and every step 2/3 choice)
    // from Liquid — the visitor would retype three steps of input. Keeping a
    // draft client-side sidesteps that entirely, and also survives an accidental
    // back/reload. sessionStorage, not localStorage: this is contact detail, so
    // it should die with the tab rather than persist on the device.
    this._draftKey = 'csf-draft:' + (this._form ? this._form.id || 'contact' : 'contact');
    this._draftFields = this.querySelectorAll('.csf__field, input[type="radio"], input[type="checkbox"], select');

    if (this.querySelector('[data-csf-success]')) {
      // Submitted cleanly — the draft has served its purpose.
      this._clearDraft();
    } else {
      this._restoreDraft();
    }

    this._saveDraftHandler = this._saveDraft.bind(this);
    this._draftFields.forEach((f) => {
      f.addEventListener('input', this._saveDraftHandler);
      f.addEventListener('change', this._saveDraftHandler);
    });

    // Errors take precedence: if the submit was rejected, that is what the
    // visitor needs to see, and it can otherwise sit above a full form off-screen
    // exactly like the success banner could.
    this._banner = this.querySelector('[data-csf-errors]') || this.querySelector('[data-csf-success]');
    if (this._banner) {
      requestAnimationFrame(() => {
        this._banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this._banner.focus({ preventScroll: true });
      });
    }
  }

  _onNext() {
    if (!this._validate()) return;

    if (this._step < this._totalSteps) {
      this._step++;
      this._render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (this._form) {
      // Genuinely on the last step now — submit explicitly rather than via
      // the button's `type`. Flipping the button to type="submit" inside
      // this same click handler (the previous approach) had the browser
      // treat *this* click as the submit too: a button's submit behavior is
      // evaluated after the click handler runs, using whatever type it has
      // by then — so the step 2→3 transition was submitting immediately.
      if (this._form.requestSubmit) this._form.requestSubmit();
      else this._form.submit();
    }
  }

  _onBack() {
    if (this._step > 1) {
      this._step--;
      this._render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  _fieldKey(f) {
    return f.name + (f.type === 'radio' ? '#' + f.value : '');
  }

  _saveDraft() {
    try {
      const data = {};
      this._draftFields.forEach((f) => {
        if (!f.name) return;
        if (f.type === 'radio' || f.type === 'checkbox') {
          if (f.checked) data[this._fieldKey(f)] = true;
        } else if (f.value) {
          data[f.name] = f.value;
        }
      });
      sessionStorage.setItem(this._draftKey, JSON.stringify(data));
    } catch (e) {
      // Private browsing / quota — a lost draft must never break the form.
    }
  }

  _restoreDraft() {
    try {
      const raw = sessionStorage.getItem(this._draftKey);
      if (!raw) return;
      const data = JSON.parse(raw);
      this._draftFields.forEach((f) => {
        if (!f.name) return;
        if (f.type === 'radio' || f.type === 'checkbox') {
          if (data[this._fieldKey(f)]) f.checked = true;
        } else if (data[f.name] !== undefined && !f.value) {
          f.value = data[f.name];
        }
      });
    } catch (e) {
      // Corrupt draft — ignore and start clean.
    }
  }

  _clearDraft() {
    try { sessionStorage.removeItem(this._draftKey); } catch (e) { /* no-op */ }
  }

  _validate() {
    const panel = this._panels[this._step - 1];
    // Every text-ish field in the panel, not just the required ones. The optional
    // Website field is type="url", so a malformed value there was also waved
    // through Next and only rejected by the browser at submit. Every `required`
    // control in this form is a .csf__field, so this set is a superset of the
    // old one — verified against the markup.
    const fields = panel.querySelectorAll('.csf__field');
    let valid = true;
    fields.forEach(field => {
      const empty = !field.value.trim();

      // An empty optional field is fine — nothing to validate.
      if (empty && !field.required) {
        this._clearFieldError(field);
        return;
      }
      // Constraint validity — type="email"/"url", a `pattern`, minlength, etc.
      // The `field.pattern &&` guard that used to sit here defeated its own
      // purpose: only the phone input carries a pattern attribute, so email was
      // never checked and "a" passed Next, only to be rejected by the browser at
      // submit time. checkValidity() already covers every constraint, and the
      // `empty` test above handles required-but-blank, so no guard is needed.
      const patternInvalid = !empty && !field.checkValidity();
      if (empty || patternInvalid) {
        this._setFieldError(field, ContactSmartForm._messageFor(field, empty));
        if (valid) field.focus();
        valid = false;
      } else {
        this._clearFieldError(field);
      }
    });
    return valid;
  }

  /**
   * Wording for an invalid field. Prefers the field's own `title`, which already
   * carries a tailored sentence on the phone input, so the copy stays editable in
   * the markup rather than duplicated here.
   */
  static _messageFor(field, empty) {
    if (empty) return 'This field is required.';
    if (field.title) return field.title;
    switch (field.type) {
      case 'email': return 'Enter a valid email address.';
      case 'tel': return 'Enter a valid phone number.';
      case 'url': return 'Enter a valid URL, including https://';
      default: return 'Please check this value.';
    }
  }

  _setFieldError(field, message) {
    field.classList.add('csf__field--error');
    field.setAttribute('aria-invalid', 'true');

    const wrap = field.closest('.csf__field-wrap');
    if (!wrap) return;

    let msgEl = wrap.querySelector('.csf__field-error-msg');
    if (!msgEl) {
      msgEl = document.createElement('p');
      msgEl.className = 'csf__field-error-msg';
      // aria-live so a message appearing after the visitor has already moved on
      // is still announced, and describedby so it is read with the field itself.
      msgEl.setAttribute('aria-live', 'polite');
      if (!field.id) field.id = `csf-field-${Math.random().toString(36).slice(2, 9)}`;
      msgEl.id = `${field.id}-error`;
      wrap.appendChild(msgEl);
    }
    field.setAttribute('aria-describedby', msgEl.id);
    msgEl.textContent = message;
    msgEl.hidden = false;
  }

  _clearFieldError(field) {
    field.classList.remove('csf__field--error');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');

    const wrap = field.closest('.csf__field-wrap');
    const msgEl = wrap && wrap.querySelector('.csf__field-error-msg');
    if (msgEl) msgEl.hidden = true;
  }

  _render() {
    // Show/hide panels
    this._panels.forEach((panel, i) => {
      panel.hidden = (i + 1) !== this._step;
    });

    // Step number active state
    this._stepNums.forEach((num, i) => {
      num.classList.toggle('csf__step-number--active', i + 1 === this._step);
    });

    // Slide pill
    if (this._pill) {
      const pct = ((this._step - 1) / this._totalSteps) * 100;
      this._pill.style.left  = `${pct}%`;
      this._pill.style.width = `${100 / this._totalSteps}%`;
    }

    // Track label active state
    this._trackLabels.forEach((lbl, i) => {
      lbl.classList.toggle('csf__step-label--active', i + 1 === this._step);
    });

    // Back button visibility
    if (this._backBtn) this._backBtn.hidden = this._step === 1;

    // Next/Submit button label. This button stays type="button" for its
    // entire lifetime — actual submission is triggered explicitly from
    // _onNext() once we're genuinely past step 3, never by the browser's
    // own submit-button activation (see the comment in _onNext() for why).
    if (this._nextBtn) {
      const isLast = this._step === this._totalSteps;
      this._nextBtn.textContent = isLast
        ? (this._nextBtn.dataset.submitLabel || 'SUBMIT REQUEST')
        : (this._nextBtn.dataset[`nextLabelStep${this._step}`] || 'NEXT');

      // Re-append double-chevron SVG if stripped (textContent= above wipes
      // it); not needed on the final "submit" label.
      if (!isLast && !this._nextBtn.querySelector('svg')) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '10'); svg.setAttribute('height', '11');
        svg.setAttribute('viewBox', '0 0 10 11'); svg.setAttribute('fill', 'none');
        svg.setAttribute('aria-hidden', 'true');
        svg.innerHTML = '<path d="M1 1l4 4.5L1 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M5 1l4 4.5L5 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>';
        this._nextBtn.appendChild(svg);
      }
    }
  }

  disconnectedCallback() {}
}

customElements.define('contact-smart-form', ContactSmartForm);
